import type { Catalog, CatalogEvent, ControlPoint, MapRecord, ProcessEventResponse, ProcessingRun, SourceChoice } from "./types";

async function parse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: "Unbekannter Fehler" }));
    throw new Error(body.detail || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getCatalog(): Promise<Catalog> {
  return parse<Catalog>(await fetch("/api/v1/catalog"));
}

export async function getEvent(id: string): Promise<CatalogEvent> {
  return parse<CatalogEvent>(await fetch(`/api/v1/events/${id}`));
}

export async function processEvent(id: string, refresh = false): Promise<ProcessEventResponse> {
  return parse<ProcessEventResponse>(await fetch(`/api/v1/events/${id}/map`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  }));
}

export async function uploadMap(file: File): Promise<MapRecord> {
  const form = new FormData();
  form.append("file", file);
  return parse<MapRecord>(await fetch("/api/v1/imports/upload", { method: "POST", body: form }));
}

export async function resolveSource(url: string): Promise<{ sources: SourceChoice[]; page_title?: string }> {
  return parse(await fetch("/api/v1/sources/resolve", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  }));
}

export async function createUrlMap(url: string): Promise<MapRecord> {
  return parse<MapRecord>(await fetch("/api/v1/imports/url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  }));
}

export async function getMap(token: string): Promise<MapRecord> {
  return parse<MapRecord>(await fetch(`/api/v1/maps/${encodeURIComponent(token)}`));
}

export async function getRun(id: string): Promise<ProcessingRun> {
  return parse<ProcessingRun>(await fetch(`/api/v1/runs/${id}`));
}

export async function improveMap(token: string): Promise<ProcessingRun> {
  return parse<ProcessingRun>(await fetch(`/api/v1/maps/${encodeURIComponent(token)}/improve`, { method: "POST" }));
}

export async function reanalyzeMap(token: string): Promise<MapRecord> {
  return parse<MapRecord>(await fetch(`/api/v1/maps/${encodeURIComponent(token)}/clusters/reanalyze`, { method: "POST" }));
}

export async function setRunLocality(id: string, locality: string): Promise<ProcessingRun> {
  return parse<ProcessingRun>(await fetch(`/api/v1/runs/${id}/locality`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locality }),
  }));
}

export async function setRunControlPoints(id: string, points: ControlPoint[]): Promise<ProcessingRun> {
  return parse<ProcessingRun>(await fetch(`/api/v1/runs/${id}/control-points`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ points }),
  }));
}

export function exportUrl(token: string, format: "kml" | "csv" | "geojson"): string {
  return `/api/v1/maps/${encodeURIComponent(token)}/exports/${format}`;
}

export function runWebsocketUrl(id: string): string {
  return websocketUrl(`/ws/runs/${id}`);
}

export function catalogWebsocketUrl(): string {
  return websocketUrl("/ws/catalog");
}

function websocketUrl(path: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${path}`;
}
